import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export type BannerVariant = 'info' | 'success' | 'warning' | 'error' | 'neutral' | 'custom'

export interface BannerView {
  id: string
  text: string
  link: string | null
  variant: BannerVariant
  bgColor: string | null
  textColor: string | null
}

export interface BannerAdminItem extends BannerView {
  active: boolean
  startsAt: string | null
  endsAt: string | null
  permissions: string[]
  bus: string[]
  priority: number
  createdAt: string
  updatedAt: string
}

export interface BannerPayload {
  text: string
  link?: string | null
  variant?: BannerVariant
  bgColor?: string | null
  textColor?: string | null
  active?: boolean
  startsAt?: string | null
  endsAt?: string | null
  permissions?: string[]
  bus?: string[]
  priority?: number
}

export const useBannerStore = defineStore('banner', () => {
  const suiteStore = useSuiteStore()

  const visible = ref<BannerView[]>([])
  const items = ref<BannerAdminItem[]>([])
  const isLoading = ref(false)
  const isSaving = ref(false)

  async function fetchVisible() {
    const res = await suiteStore.suiteFetch('banners', { method: 'GET', credentials: 'include' })
    if (res && typeof res !== 'boolean' && res.ok) {
      visible.value = (await res.json()) ?? []
    }
  }

  async function fetchAll() {
    isLoading.value = true
    try {
      const res = await suiteStore.suiteFetch('banners/admin', { method: 'GET', credentials: 'include' })
      if (res && typeof res !== 'boolean' && res.ok) {
        items.value = (await res.json()) ?? []
      }
    } finally {
      isLoading.value = false
    }
  }

  async function create(payload: BannerPayload): Promise<boolean> {
    return save('banners/admin', 'POST', payload)
  }

  async function update(id: string, payload: BannerPayload): Promise<boolean> {
    return save(`banners/admin/${id}`, 'PUT', payload)
  }

  async function save(path: string, method: 'POST' | 'PUT', payload: BannerPayload): Promise<boolean> {
    isSaving.value = true
    try {
      const res = await suiteStore.suiteFetch(path, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const ok = !!res && typeof res !== 'boolean' && res.ok
      if (ok) await fetchAll()
      return ok
    } finally {
      isSaving.value = false
    }
  }

  async function remove(id: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`banners/admin/${id}`, { method: 'DELETE', credentials: 'include' })
    const ok = !!res && typeof res !== 'boolean' && res.ok
    if (ok) await fetchAll()
    return ok
  }

  return { visible, items, isLoading, isSaving, fetchVisible, fetchAll, create, update, remove }
})
